import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  X,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  Loader2,
  Download,
  Pencil,
  PlayCircle,
  CheckSquare,
  Keyboard,
  ArrowLeftRight,
  ShieldAlert,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/ui/components/ui/button";

import { cn } from "@/ui/lib/utils";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";

import BinaryFamilyControl from "./binary-family-control";
import PipelineGroupSection from "./pipeline-group-section";
import ChunkProgressList from "./chunk-progress-list";
import ExportPanel from "./export-panel";
import {
  useBulkSelectionStore,
  countStagedChanges,
} from "../../stores/useBulkSelectionStore";
import { useGetClassificationTaxonomy } from "../../api";
import { DEFAULT_TASK_KIND } from "../../types/project";
import { useBatchChunkedReview } from "../../hooks/useBatchChunkedReview";
import {
  buildCurrentFamilyState,
  memberToCanonical,
} from "../../utils/taxonomy";
import type {
  ClassificationReviewItem,
  BatchReviewItem,
  ReviewCommand,
} from "../../types/review";
import type { FamilyKey, FamilyMemberSelection } from "../../types/taxonomy";
import {
  buildPipelineGroups,
  familyForShortcut,
  FAMILY_SHORTCUTS,
  getMemberDisplayOrder,
  groupedShortcutHints,
  nextCycleSelection,
} from "../../utils/pipeline-groups";

/** Footer hint shorthands are internal tokens, mapped to display text here. */
const SHORTCUT_GROUP_KEYS: Record<
  string,
  "pipelineGroups.short.core" | "pipelineGroups.short.gate" | "pipelineGroups.short.env"
> = {
  core: "pipelineGroups.short.core",
  gate: "pipelineGroups.short.gate",
  env: "pipelineGroups.short.env",
};

/* ──────────────────────────────────────────────────────────────────
 * BulkActionsBar — sticky bottom bar for bulk review & export.
 *
 * Two intents:
 *   Walk through  = "I want to LOOK at each one" → per-image carousel
 *   Bulk apply    = "I already know, stamp them all" → one POST
 *
 * Keyboard (same shortcuts as detail/carousel):
 *   B C G / 1 2 3 / N R D M   cycle family draft (members[] → none)
 *   Q                         reset drafts
 *
 * UX improvements over naive implementation:
 *   - Impact counter: "Will change 8 of 12" (real buildBatchItems)
 *   - Inline preview: collapsed bar shows staged drafts as chips
 *   - Confirmation step before destructive bulk apply
 *   - Select all visible button
 * ────────────────────────────────────────────────────────────────── */

type ExpandMode = "none" | "review" | "export" | "confirm";

interface BulkActionsBarProps {
  loadedItems: ClassificationReviewItem[];
  onWalkThrough: (imageIds: number[]) => void;
  scopeLocationId?: number;
  scopeMachineIds: Set<number>;
}

const BulkActionsBar = ({
  loadedItems,
  onWalkThrough,
  scopeLocationId,
  scopeMachineIds,
}: BulkActionsBarProps) => {
  const { t } = useTranslation("mlExports");
  const ids = useBulkSelectionStore((s) => s.ids);
  const drafts = useBulkSelectionStore((s) => s.drafts);
  const dumpImageValidOverride = useBulkSelectionStore(
    (s) => s.dumpImageValidOverride,
  );
  const clear = useBulkSelectionStore((s) => s.clear);
  const addMany = useBulkSelectionStore((s) => s.addMany);
  const stageFamily = useBulkSelectionStore((s) => s.stageFamily);
  const clearDrafts = useBulkSelectionStore((s) => s.clearDrafts);
  const setDumpImageValid = useBulkSelectionStore((s) => s.setDumpImageValid);
  const stagedCount = useBulkSelectionStore(countStagedChanges);

  const [expanded, setExpanded] = useState<ExpandMode>("none");
  // Composite id (project_key + destination) — project_key alone isn't unique.
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [activeTaskKind, setActiveTaskKind] = useState(DEFAULT_TASK_KIND);
  const [batchError, setBatchError] = useState<{
    reason?: string;
    imageId?: number;
    labelId?: number;
    detail?: string;
  } | null>(null);

  const { data: taxonomy } = useGetClassificationTaxonomy();

  const pipelineGroups = useMemo(() => buildPipelineGroups(taxonomy), [taxonomy]);

  /** label canonical → hex color from taxonomy */
  const labelColors = useMemo(() => {
    if (!taxonomy) return {};
    const map: Record<string, string> = {};
    for (const f of taxonomy.families) {
      for (const member of f.members) {
        if (member.color) map[member.canonical] = member.color;
      }
    }
    return map;
  }, [taxonomy]);

  const chunkedReview = useBatchChunkedReview();

  /** The full export selection, materialized once for the export panel. */
  const exportIds = useMemo(() => [...ids], [ids]);

  const loadedItemsById = useMemo(() => {
    const map = new Map<number, ClassificationReviewItem>();
    for (const item of loadedItems) map.set(item.image_id, item);
    return map;
  }, [loadedItems]);

  const totalSelected = ids.size;
  const loadedSelected = useMemo(
    () => [...ids].filter((id) => loadedItemsById.has(id)).length,
    [ids, loadedItemsById],
  );
  const hiddenSelected = totalSelected - loadedSelected;

  // Are all visible items selected?
  const allVisibleSelected =
    loadedItems.length > 0 &&
    loadedItems.every((item) => ids.has(item.image_id));

  // Count selected images that have ONLY legacy labels
  const legacyCount = useMemo(() => {
    if (!taxonomy?.other_labels) return 0;
    const legacyCanonicals = new Set(
      taxonomy.other_labels
        .filter((l) => l.category === "legacy")
        .map((l) => l.canonical),
    );
    if (legacyCanonicals.size === 0) return 0;
    let count = 0;
    for (const imageId of ids) {
      const item = loadedItemsById.get(imageId);
      if (!item) continue;
      const labels = item.labels ?? [];
      if (labels.length > 0 && labels.every((l) => legacyCanonicals.has(l.final_label))) {
        count++;
      }
    }
    return count;
  }, [ids, loadedItemsById, taxonomy]);

  // Distribution per family across loaded selection — one count per member
  const distribution = useMemo(() => {
    const result: Record<string, { counts: number[]; unknown: number }> = {};
    if (!taxonomy) return result;
    for (const family of taxonomy.families) {
      result[family.key] = {
        counts: family.members.map(() => 0),
        unknown: 0,
      };
    }
    for (const imageId of ids) {
      const item = loadedItemsById.get(imageId);
      if (!item) continue;
      const state = buildCurrentFamilyState(item.labels ?? [], taxonomy);
      for (const family of taxonomy.families) {
        const entry = state.get(family.key);
        if (!entry) result[family.key].unknown += 1;
        else result[family.key].counts[entry.memberIndex] += 1;
      }
    }
    return result;
  }, [ids, loadedItemsById, taxonomy]);

  useEffect(() => {
    if (totalSelected === 0) setExpanded("none");
  }, [totalSelected]);

  // ─── Live impact preview ──────────────────────────────────────────
  const impact = useMemo(() => {
    const stats = {
      hidden: 0,
      legacyOnly: 0,
      creates: 0,
      noOp: 0,
      alreadyHuman: 0,
      applicable: 0,
    };
    if (!taxonomy) return { items: [] as BatchReviewItem[], stats };

    const legacyCanonicals = new Set(
      (taxonomy.other_labels ?? [])
        .filter((l) => l.category === "legacy")
        .map((l) => l.canonical),
    );

    const activeDrafts = Object.entries(drafts).filter(
      ([, selection]) => selection !== undefined && selection !== "none",
    );

    const items: BatchReviewItem[] = [];
    for (const imageId of ids) {
      const item = loadedItemsById.get(imageId);
      if (!item) {
        stats.hidden += 1;
        continue;
      }

      const labels = item.labels ?? [];
      const isLegacyOnly =
        labels.length > 0 &&
        labels.every((l) => legacyCanonicals.has(l.final_label));

      if (isLegacyOnly) stats.legacyOnly += 1;

      // Legacy images: skip label commands but still allow dump_image_valid
      const currentState = isLegacyOnly
        ? new Map()
        : buildCurrentFamilyState(labels, taxonomy);
      const commands: ReviewCommand[] = [];
      for (const [familyKey, selection] of activeDrafts) {
        // Legacy-only images are read-only for classification — don't
        // create labels on them either.
        if (isLegacyOnly) continue;
        const family = taxonomy.families.find((f) => f.key === familyKey);
        if (!family) continue;
        const canonical = memberToCanonical(family, selection!);
        if (!canonical) continue;
        const entry = currentState.get(familyKey);
        if (!entry) {
          // No model prediction for this family → create a human label.
          commands.push({ type: "create", human_label: canonical });
          stats.creates += 1;
          stats.applicable += 1;
          continue;
        }
        if (entry.memberIndex === selection) {
          stats.noOp += 1;
          continue;
        }
        if (entry.label.origin === "human") {
          stats.alreadyHuman += 1;
          continue;
        }
        commands.push({
          type: "update",
          id: entry.label.id,
          human_label: canonical,
        });
        stats.applicable += 1;
      }
      if (commands.length > 0 || dumpImageValidOverride !== undefined) {
        items.push({
          image_id: imageId,
          commands,
          ...(dumpImageValidOverride !== undefined && {
            dump_image_valid: dumpImageValidOverride,
          }),
        });
      }
    }
    return { items, stats };
  }, [taxonomy, drafts, ids, loadedItemsById, dumpImageValidOverride]);

  // ─── Keyboard for bulk panel ─────────────────────────────────────
  useEffect(() => {
    if (expanded !== "review" || !taxonomy) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      )
        return;

      const k = e.key.toLowerCase();

      // Pipeline shortcuts: B C G core · 1 2 3 gate · N R D M env
      const family = familyForShortcut(taxonomy, k);
      if (family) {
        e.preventDefault();
        const currentDraft = drafts[family.key as FamilyKey];
        // Cycle: none → members[0] → … → members[N−1] → none
        const next: FamilyMemberSelection = nextCycleSelection(
          family.members.length,
          currentDraft ?? "none",
        );
        stageFamily(family.key as FamilyKey, next);
        return;
      }

      if (k === "q") {
        e.preventDefault();
        clearDrafts();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [expanded, taxonomy, drafts, stageFamily, clearDrafts]);

  // ─── Staged drafts as inline chips for collapsed bar ─────────────
  const draftChips = useMemo(() => {
    if (!taxonomy) return [];
    return Object.entries(drafts)
      .filter(([, selection]) => selection !== undefined && selection !== "none")
      .map(([familyKey, selection]) => {
        const family = taxonomy.families.find((f) => f.key === familyKey);
        const member =
          family && selection !== "none" && selection !== undefined
            ? family.members[selection]
            : undefined;
        if (!family || !member) return null;
        return {
          key: familyKey,
          familyName: family.display_name,
          memberName: member.display_name,
        };
      })
      .filter(Boolean) as {
      key: string;
      familyName: string;
      memberName: string;
    }[];
  }, [taxonomy, drafts]);

  if (totalSelected === 0) return null;

  // ─── Handlers ────────────────────────────────────────────────────

  const handleSelectAllVisible = () => {
    addMany(loadedItems.map((item) => item.image_id));
  };

  const handleClearHidden = () => {
    const next = [...ids].filter((id) => loadedItemsById.has(id));
    clear();
    useBulkSelectionStore.getState().addMany(next);
  };

  const handleRequestApply = () => {
    if (impact.items.length === 0) {
      const reasons: string[] = [];
      if (impact.stats.legacyOnly)
        reasons.push(
          t("bulk.reason.legacy", { value: formatNumber(impact.stats.legacyOnly) }),
        );
      if (impact.stats.alreadyHuman)
        reasons.push(
          t("bulk.reason.alreadyCorrected", {
            value: formatNumber(impact.stats.alreadyHuman),
          }),
        );
      if (impact.stats.noOp)
        reasons.push(
          t("bulk.reason.alreadyOnTarget", { value: formatNumber(impact.stats.noOp) }),
        );
      if (impact.stats.hidden)
        reasons.push(
          t("bulk.reason.notLoaded", { value: formatNumber(impact.stats.hidden) }),
        );
      toast.error(
        reasons.length > 0
          ? t("bulk.toast.nothingToApply", { reasons: reasons.join(" · ") })
          : t("bulk.toast.pickASide"),
      );
      return;
    }
    setExpanded("confirm");
  };

  const handleConfirmApply = async () => {
    if (impact.items.length === 0) return;
    setBatchError(null);

    const success = await chunkedReview.execute(impact.items);

    if (success) {
      toast.success(
        t("bulk.toast.applied", {
          count: impact.items.length,
          value: formatNumber(impact.items.length),
        }),
      );
      clearDrafts();
      clear();
      chunkedReview.reset();
      setExpanded("none");
    } else {
      const failedChunk = chunkedReview.chunks.find((c) => c.status === "failed");
      if (failedChunk) {
        setBatchError({
          detail: failedChunk.error,
          imageId: failedChunk.imageId,
        });
      }
    }
  };

  const handleRetryChunk = async () => {
    setBatchError(null);
    const success = await chunkedReview.retryFromFailed();
    if (success) {
      toast.success(
        t("bulk.toast.applied", {
          count: impact.items.length,
          value: formatNumber(impact.items.length),
        }),
      );
      clearDrafts();
      clear();
      chunkedReview.reset();
      setExpanded("none");
    }
  };

  return (
    <div
      className={cn(
        "sticky bottom-0 z-20 border-t bg-background shadow-lg",
        "transition-all duration-200",
      )}
    >
      {/* ─── Review panel ─────────────────────────────────────── */}
      {expanded === "review" && taxonomy && (
        <div className="border-b bg-amber-50/50 dark:bg-amber-950/10 px-6 py-4 max-h-[50vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-semibold">{t("bulk.title")}</h3>
              <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                {t("bulk.imageCount", {
                  count: totalSelected,
                  value: formatNumber(totalSelected),
                })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden md:inline-flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
                <Keyboard className="h-3 w-3 shrink-0" />
                {groupedShortcutHints().map((group) => (
                  <span key={group.label} className="inline-flex items-center gap-0.5">
                    {group.keys.map((key) => (
                      <kbd
                        key={key}
                        className="inline-flex items-center justify-center h-4 min-w-4 px-0.5 rounded bg-muted border text-[9px] font-mono font-semibold uppercase"
                      >
                        {key}
                      </kbd>
                    ))}
                    <span className="opacity-70">
                      {t(SHORTCUT_GROUP_KEYS[group.label] ?? "pipelineGroups.short.core")}
                    </span>
                  </span>
                ))}
                <span className="inline-flex items-center gap-0.5 ml-1">
                  <kbd className="inline-flex items-center justify-center h-4 min-w-4 px-0.5 rounded bg-muted border text-[9px] font-mono font-semibold">
                    Q
                  </kbd>
                  <span className="opacity-70">{t("bulk.resetHint")}</span>
                </span>
              </span>
              <button
                onClick={() => setExpanded("none")}
                className="p-1 rounded hover:bg-accent"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Warnings */}
          {legacyCount > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span>
                {t("bulk.legacyWarning", {
                  legacy: formatNumber(legacyCount),
                  total: formatNumber(totalSelected),
                })}
              </span>
            </div>
          )}
          {taxonomy.families.every(
            (f) =>
              (distribution[f.key]?.counts ?? []).reduce(
                (sum, c) => sum + c,
                0,
              ) === 0,
          ) && legacyCount === 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span>
                {t("bulk.noPredictionsWarning")}
              </span>
            </div>
          )}

          <div className="space-y-4">
            {pipelineGroups.map((group) => (
              <PipelineGroupSection key={group.key} group={group}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {group.families.map((family) => {
                    const dist = distribution[family.key] ?? {
                      counts: family.members.map(() => 0),
                      unknown: 0,
                    };
                    const labelTotal = dist.counts.reduce(
                      (sum, c) => sum + c,
                      0,
                    );
                    const memberSel: FamilyMemberSelection =
                      drafts[family.key as FamilyKey] ?? "none";
                    const caption =
                      labelTotal === 0 ? (
                        <span className="text-muted-foreground">
                          {t("bulk.noPredictionsCaption")}
                        </span>
                      ) : (
                        <span>
                          {getMemberDisplayOrder(family)
                            .map(
                              (i) =>
                                `${dist.counts[i] ?? 0} ${family.members[i].display_name.toLowerCase()}`,
                            )
                            .join(" · ")}
                          {dist.unknown > 0 &&
                            t("bulk.notAvailable", {
                              value: formatNumber(dist.unknown),
                            })}
                        </span>
                      );
                    return (
                      <BinaryFamilyControl
                        key={family.key}
                        family={family}
                        selectedIndex={memberSel}
                        staged={memberSel !== "none"}
                        displayOrder={getMemberDisplayOrder(family)}
                        shortcutKey={FAMILY_SHORTCUTS[family.key]}
                        caption={caption}
                        onChange={(selection) => {
                          setBatchError(null);
                          stageFamily(family.key as FamilyKey, selection);
                        }}
                      />
                    );
                  })}
                </div>
              </PipelineGroupSection>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-amber-500/10 pt-3">
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-muted-foreground">{t("bulk.frameValidity")}</span>
              <div className="inline-flex rounded-md border border-border overflow-hidden">
                <button
                  onClick={() => setDumpImageValid(undefined)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium transition-colors",
                    dumpImageValidOverride === undefined
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted/50",
                  )}
                >
                  {t("bulk.validity.skip")}
                </button>
                <button
                  onClick={() => setDumpImageValid(true)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium border-x border-border transition-colors",
                    dumpImageValidOverride === true
                      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                      : "text-muted-foreground hover:bg-muted/50",
                  )}
                >
                  {t("bulk.validity.valid")}
                </button>
                <button
                  onClick={() => setDumpImageValid(false)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium transition-colors",
                    dumpImageValidOverride === false
                      ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                      : "text-muted-foreground hover:bg-muted/50",
                  )}
                >
                  {t("bulk.validity.invalid")}
                </button>
              </div>
            </div>
          </div>

          {/* Impact preview */}
          <div className="mt-4 rounded-lg border border-amber-500/20 bg-background p-3">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                {stagedCount === 0 ? (
                  <span className="text-muted-foreground">
                    {t("bulk.impact.pickSide")}
                  </span>
                ) : impact.items.length === 0 ? (
                  <span className="text-muted-foreground">
                    {t("bulk.impact.noChange")}
                    {impact.stats.legacyOnly > 0 && (
                      <span className="text-[11px]">
                        {" · "}
                        {t("bulk.reason.legacy", {
                          value: formatNumber(impact.stats.legacyOnly),
                        })}
                      </span>
                    )}
                    {impact.stats.noOp > 0 && (
                      <span className="text-[11px]">
                        {" · "}
                        {t("bulk.reason.alreadyOnTarget", {
                          value: formatNumber(impact.stats.noOp),
                        })}
                      </span>
                    )}
                    {impact.stats.alreadyHuman > 0 && (
                      <span className="text-[11px]">
                        {" · "}
                        {t("bulk.reason.alreadyCorrected", {
                          value: formatNumber(impact.stats.alreadyHuman),
                        })}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="font-semibold text-amber-700 dark:text-amber-300">
                    {t("bulk.impact.willChange", {
                      changed: formatNumber(impact.items.length),
                      total: formatNumber(totalSelected),
                    })}
                    {impact.stats.creates > 0 && (
                      <span className="text-[11px] font-normal text-violet-600 dark:text-violet-400">
                        {" · "}
                        {t("bulk.impact.willCreate", {
                          count: impact.stats.creates,
                          value: formatNumber(impact.stats.creates),
                        })}
                      </span>
                    )}
                    {impact.stats.legacyOnly > 0 && (
                      <span className="text-[11px] font-normal text-muted-foreground">
                        {" · "}
                        {t("bulk.impact.skippedLegacy", {
                          value: formatNumber(impact.stats.legacyOnly),
                        })}
                      </span>
                    )}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearDrafts}
                  disabled={stagedCount === 0}
                  className="text-xs h-8"
                >
                  {t("detail.footer.reset")}
                  <kbd className="ml-1 text-[9px] opacity-50">Q</kbd>
                </Button>
                <Button
                  size="sm"
                  disabled={stagedCount === 0 || impact.items.length === 0}
                  onClick={handleRequestApply}
                  className={cn(
                    "h-8",
                    impact.items.length > 0 &&
                      "bg-amber-500 hover:bg-amber-600 text-white",
                  )}
                >
                  {t("bulk.applyTo", {
                    count: impact.items.length,
                    value: formatNumber(impact.items.length),
                  })}
                </Button>
              </div>
            </div>
          </div>

          {batchError && (
            <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600 dark:text-red-400">
              <div className="flex items-center gap-2 font-medium mb-1">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {t("bulk.error.title")}
              </div>
              <p>
                {batchError.detail ?? t("bulk.error.rolledBack")}
                {batchError.imageId && (
                  <span className="font-mono ml-1">
                    {t("chunkProgress.imageRef", { id: batchError.imageId })}
                  </span>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ─── Confirmation step ────────────────────────────────── */}
      {expanded === "confirm" && (
        <div className="border-b bg-amber-50 dark:bg-amber-950/20 px-6 py-5">
          <div className="max-w-lg mx-auto text-center space-y-4">
            <div className="flex justify-center">
              <div className="p-3 rounded-full bg-amber-500/10">
                <ShieldAlert className="h-8 w-8 text-amber-500" />
              </div>
            </div>
            <div>
              <h3 className="text-base font-semibold mb-1">
                {t("bulk.confirm.title", {
                  count: impact.items.length,
                  value: formatNumber(impact.items.length),
                })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("bulk.confirm.description")}
              </p>
            </div>

            {/* Summary of what will change */}
            <div className="flex flex-wrap justify-center gap-2">
              {draftChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20"
                >
                  <ArrowLeftRight className="h-3 w-3" />
                  {chip.familyName} → {chip.memberName}
                </span>
              ))}
            </div>

            <ChunkProgressList chunks={chunkedReview.chunks} />

            <div className="flex items-center justify-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  chunkedReview.reset();
                  setExpanded("review");
                }}
                disabled={chunkedReview.isRunning}
              >
                {t("bulk.confirm.back")}
              </Button>
              {chunkedReview.chunks.some((c) => c.status === "failed") ? (
                <Button
                  size="sm"
                  onClick={handleRetryChunk}
                  disabled={chunkedReview.isRunning}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  {t("bulk.confirm.retry")}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={handleConfirmApply}
                  disabled={chunkedReview.isRunning}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {chunkedReview.isRunning ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Pencil className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {t("bulk.confirm.submit", {
                    value: formatNumber(impact.items.length),
                  })}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Export panel ─────────────────────────────────────── */}
      {expanded === "export" && (
        <ExportPanel
          imageIds={exportIds}
          hiddenSelected={hiddenSelected}
          scopeLocationId={scopeLocationId}
          scopeMachineIds={scopeMachineIds}
          families={taxonomy?.families}
          labelColors={labelColors}
          selectedUid={selectedUid}
          onSelectUid={setSelectedUid}
          activeTaskKind={activeTaskKind}
          onTaskKindChange={setActiveTaskKind}
          onClose={() => setExpanded("none")}
          onExported={() => {
            clear();
            setExpanded("none");
          }}
        />
      )}

      {/* ─── Collapsed bar ────────────────────────────────────── */}
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3 text-sm min-w-0">
          <span className="font-semibold shrink-0">
            {t("bulk.selectedCount", {
              count: totalSelected,
              value: formatNumber(totalSelected),
            })}
          </span>

          {/* Inline draft preview chips */}
          {draftChips.length > 0 && expanded === "none" && (
            <>
              <span className="text-muted-foreground">·</span>
              <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                {draftChips.map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0"
                  >
                    {chip.familyName} → {chip.memberName}
                  </span>
                ))}
                {impact.items.length > 0 && (
                  <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400 shrink-0">
                    {t("bulk.willChangeChip", {
                      value: formatNumber(impact.items.length),
                    })}
                  </span>
                )}
              </div>
            </>
          )}

          {hiddenSelected > 0 && draftChips.length === 0 && (
            <>
              <span className="text-muted-foreground">·</span>
              <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300 text-xs">
                <AlertTriangle className="h-3 w-3" />
                {t("bulk.notInView", { value: formatNumber(hiddenSelected) })}
              </span>
              <button
                onClick={handleClearHidden}
                className="text-xs text-primary hover:underline"
              >
                {t("bulk.clearHidden")}
              </button>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Select / deselect visible */}
          {loadedItems.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={allVisibleSelected ? clear : handleSelectAllVisible}
              className="text-xs"
            >
              <CheckSquare className="h-3.5 w-3.5 mr-1" />
              {allVisibleSelected
                ? t("bulk.deselectAll")
                : t("bulk.selectVisible")}
            </Button>
          )}

          <Button variant="ghost" size="sm" onClick={clear}>
            {t("bulk.clear")}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => onWalkThrough([...ids])}
            title={t("bulk.walkThroughTitle")}
          >
            <PlayCircle className="h-3.5 w-3.5 mr-1.5" />
            {t("bulk.walkThrough")}
          </Button>
          <Button
            variant={expanded === "review" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setExpanded((m) =>
                m === "review" ? "none" : "review",
              )
            }
            title={t("bulk.bulkApplyTitle")}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            {t("bulk.bulkApply")}
            {expanded === "review" ? (
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 ml-1" />
            )}
          </Button>
          <Button
            variant={expanded === "export" ? "default" : "outline"}
            size="sm"
            onClick={() =>
              setExpanded((m) =>
                m === "export" ? "none" : "export",
              )
            }
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {t("bulk.export")}
            {expanded === "export" ? (
              <ChevronDown className="h-3.5 w-3.5 ml-1" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 ml-1" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkActionsBar;
